import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Notice } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import {
  api,
  type QuotaResolution,
  type Schedules,
  type SendingStatus,
  type TickResult,
} from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Schedules and quotas (§21, §22, §33).
 *
 * **This screen shows the inputs to the allowance, never the allowance.** §21 resolves the real
 * limit at send time as the most restrictive of six rules — system, campaign, domain, mailbox,
 * provider and contact — and the answer changes as the day goes on.
 *
 * A single "40 per day" figure here would be believed and would then disagree with what the
 * dispatcher actually does. That disagreement would be invisible until real mail moved, which is
 * the worst possible moment to discover a screen was guessing.
 *
 * So every constraint is listed with where it comes from, and nothing is multiplied together.
 *
 * **One number on this screen is resolved, and it is the only one that may be.** "The engine" panel
 * asks `GET /sending/quota` for a named mailbox and a named campaign, which is the only pair for
 * which §21's allowance exists at all — and what comes back carries every rule that produced it,
 * including the provider's technical ceiling marked as *not applied*. A resolved answer that shows
 * its working is a different object from a computed guess.
 */
export function SchedulesScreen() {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const data = useQuery({
    queryKey: ['schedules'],
    queryFn: () => api.get<Schedules>('/schedules'),
    enabled: can('view_contacts'),
  })

  const engine = useQuery({
    queryKey: ['sending', 'status'],
    queryFn: () => api.get<SendingStatus>('/sending/status'),
    enabled: can('view_contacts'),
    // Only while a sweep could be in flight. Polling a queue that nothing drains is noise.
    refetchInterval: (query) =>
      query.state.data?.scheduler.running || (query.state.data?.queue.due ?? 0) > 0
        ? 5000
        : false,
  })

  const tick = useMutation({
    mutationFn: () => api.post<TickResult>('/sending/tick', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sending'] })
      void queryClient.invalidateQueries({ queryKey: ['schedules'] })
    },
  })

  // Declared here rather than beside its panel: hooks may not sit after an early return, and this
  // screen has three of them.
  const firstMailboxId = data.data?.mailboxes[0]?.id ?? null
  const firstCampaignId =
    data.data?.campaigns.find((c) => c.state === 'RUNNING')?.id ??
    data.data?.campaigns[0]?.id ??
    null

  const quota = useQuery({
    queryKey: ['sending', 'quota', firstMailboxId, firstCampaignId],
    queryFn: () =>
      api.get<QuotaResolution>(
        `/sending/quota?mailboxId=${firstMailboxId!}&campaignId=${firstCampaignId!}`,
      ),
    enabled: can('view_contacts') && !!firstMailboxId && !!firstCampaignId,
  })

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Schedules" description="When mail may go out, and how much." />
        <Panel>
          <Blank icon="denied" title="You do not have access to schedules" />
        </Panel>
      </Screen>
    )
  }

  if (data.isLoading) {
    return (
      <Screen>
        <ScreenHeader title="Schedules" description="Loading…" />
      </Screen>
    )
  }

  if (data.isError || !data.data) {
    return (
      <Screen>
        <ScreenHeader title="Schedules" />
        <Panel>
          <Blank
            icon="error"
            tone="bad"
            title="Schedules could not be read"
            action={<Button onClick={() => void data.refetch()}>Try again</Button>}
          />
        </Panel>
      </Screen>
    )
  }

  const s = data.data
  const e = engine.data

  const messageStates = Object.entries(e?.messages ?? {}).filter(([, count]) => count > 0)
  const lastTick = e?.scheduler.lastTick ?? null

  // §21's allowance exists only for a named mailbox on a named campaign, so the resolved panel
  // appears only when both exist. Picking one arbitrarily would put a number on screen that is
  // true of one pair and read as true of all of them.
  const quotaTarget = s.mailboxes[0] ?? null
  const quotaCampaign = s.campaigns.find((c) => c.state === 'RUNNING') ?? s.campaigns[0] ?? null

  return (
    <Screen>
      <ScreenHeader
        title="Schedules"
        description="Every constraint that will apply when sending starts, and where each one comes from. The allowance itself is resolved at send time as the most restrictive of all of them — it is deliberately not computed here."
      />

{/* §99. The single most consequential fact on this screen, so it is the first thing on it. */}
      {e?.allSendsAreDryRuns !== false && (
        <Notice tone="info" label="dry run">
          Every message goes through the null provider. The pipeline runs end to end — quota,
          rendering, preflight, MIME, unsubscribe headers — and records what it would have sent.
          Nothing reaches a recipient until a provider adapter exists (Phase 2).
        </Notice>
      )}

      {e && !e.scheduler.enabled && (
        <Notice tone="warn">
          The scheduler is off, so nothing is swept automatically.{' '}
          <span className="text-muted">
            SCHEDULER_ENABLED is not set to true — deliberate, so a process does not start
            dispatching the moment it boots.
          </span>{' '}
          {can('operate_dispatch')
            ? 'Run one sweep by hand below.'
            : 'Running a sweep needs operate_dispatch.'}
        </Notice>
      )}

      <Panel
        title="The engine"
        description="§22's claim column, and what the last sweep did"
        actions={
          can('operate_dispatch') ? (
            <Button
              onClick={() => tick.mutate()}
              disabled={tick.isPending}
              aria-label="Run one plan-and-dispatch sweep now"
            >
              {tick.isPending ? 'Sweeping…' : 'Run one sweep'}
            </Button>
          ) : undefined
        }
      >
        <div>
          <Row label="Scheduler">
            {e ? (
              e.scheduler.enabled ? (
                <>
                  on, every {Math.round(e.scheduler.intervalMs / 1000)}s
                  <span className="text-faint">
                    {' '}
                    ·{' '}
                    {e.scheduler.driver === 'redis'
                      ? 'dispatch queue'
                      : 'in this process'}{' '}
                    · worker {e.scheduler.worker}
                  </span>
                </>
              ) : (
                <span className="text-muted">off — swept only by hand</span>
              )
            ) : (
              <span className="text-faint">—</span>
            )}
          </Row>
          <Row label="Actions due now">
            <span className="tabular-nums">{(e?.queue.due ?? 0).toLocaleString()}</span>
          </Row>
          <Row label="Scheduled, not yet due">
            <span className="tabular-nums">{(e?.queue.scheduled ?? 0).toLocaleString()}</span>
          </Row>
          <Row label="In progress">
            <span className="tabular-nums">{(e?.queue.inProgress ?? 0).toLocaleString()}</span>
          </Row>
          <Row label="Next action at">
            {e?.queue.nextActionAt ? (
              new Date(e.queue.nextActionAt).toLocaleString()
            ) : (
              <span className="text-faint">nothing scheduled</span>
            )}
          </Row>
          {/* §24's Redis queue, which answers a different question from the rows above: those are
              work the database knows about, this is work already handed to a worker. A gap between
              them that does not close is what a stalled worker looks like. */}
          <Row label="Dispatch queue (§24)">
            {e?.queue.redis ? (
              <span className="tabular-nums">
                {e.queue.redis.waiting} waiting · {e.queue.redis.active} active ·{' '}
                {e.queue.redis.delayed} delayed · {e.queue.redis.failed} failed
                {e.queue.concurrency !== null && (
                  <span className="text-faint">
                    {' '}
                    — {e.queue.concurrency} at a time
                  </span>
                )}
              </span>
            ) : (
              // Not "0 waiting". There is no queue, and saying nothing is waiting would read as a
              // healthy empty queue rather than as no queue at all (§49). Which of the two
              // reasons it is matters to whoever is reading: one is a switch, the other is a
              // deliberate choice not to run Redis (P054).
              <span className="text-faint">
                {e?.scheduler.enabled
                  ? 'no queue — the sweep dispatches in the API process (QUEUE_DRIVER=timer)'
                  : 'not running — the sweep starts with SCHEDULER_ENABLED'}
              </span>
            )}
          </Row>
          <Row label="Messages">
            {messageStates.length === 0 ? (
              <span className="text-faint">none — nothing has been rendered yet</span>
            ) : (
              <span className="tabular-nums">
                {messageStates.map(([state, count]) => `${count} ${state.toLowerCase()}`).join(' · ')}
              </span>
            )}
          </Row>
          <Row label="Failed jobs (§52)">
            {(e?.deadLetters.total ?? 0) === 0 ? (
              <span className="text-faint">none</span>
            ) : (
              <span className="text-bad tabular-nums">
                {e!.deadLetters.total} unresolved ·{' '}
                {e!.deadLetters.byQueue.map((q) => `${q.queue} ${q.unresolved}`).join(', ')}
              </span>
            )}
          </Row>
          {lastTick && (
            <Row label="Last sweep">
              {lastTick.organizations.length === 0 ? (
                <span className="text-muted">
                  {lastTick.skipped.length > 0 ? lastTick.skipped.join('; ') : 'nothing to do'}
                </span>
              ) : (
                <span className="tabular-nums">
                  {lastTick.organizations
                    .map(
                      (o) =>
                        `planned ${o.planned} · sent ${o.sent} · refused ${o.refused} · deferred ${o.deferred} · failed ${o.failed}`,
                    )
                    .join(' | ')}
                  <span className="text-faint"> in {lastTick.durationMs}ms</span>
                </span>
              )}
            </Row>
          )}
        </div>
      </Panel>

      {quota.data && (
        <Panel
          title="§21, resolved"
          description={`For ${quotaTarget?.email ?? 'this mailbox'} on "${quotaCampaign?.name ?? 'this campaign'}" — the most restrictive rule wins, and every rule is shown`}
        >
          <div>
            <Row label="May send right now">
              {quota.data.allowed ? (
                <span className="text-ok">yes, {quota.data.remaining} remaining</span>
              ) : (
                <span className="text-bad">no</span>
              )}
            </Row>
            {quota.data.effective && (
              <Row label="Effective limit">
                <span className="tabular-nums">
                  {quota.data.effective.limit} per{' '}
                  {quota.data.effective.window.toLowerCase().replace('rolling_', 'rolling ')}
                </span>{' '}
                <span className="text-muted">
                  from {quota.data.effective.source.toLowerCase()}
                </span>
              </Row>
            )}
            {quota.data.nextEligibleAt && !quota.data.allowed && (
              <Row label="Next eligible">
                {new Date(quota.data.nextEligibleAt).toLocaleString()}
              </Row>
            )}
          </div>

          <Table>
            <THead>
              <Th>Rule</Th>
              <Th width={130}>Window</Th>
              <Th width={90} align="right">
                Limit
              </Th>
              <Th width={90} align="right">
                Used
              </Th>
              <Th width={90} align="right">
                Left
              </Th>
            </THead>
            <TBody>
              {quota.data.rules.map((rule, index) => (
                <Tr key={`${rule.source}-${rule.window}-${index}`}>
                  <Td>
                    {rule.source.toLowerCase().replace('_', ' ')}
                    {!rule.applies && (
                      <span className="text-faint"> · reported, never applied (§21)</span>
                    )}
                  </Td>
                  <Td className="text-muted">
                    {rule.window.toLowerCase().replace('rolling_', 'rolling ')}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {rule.limit}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {rule.used}
                  </Td>
                  <Td
                    align="right"
                    className={rule.applies && rule.remaining === 0 ? 'text-bad tabular-nums' : 'tabular-nums'}
                  >
                    {rule.remaining}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>

          {quota.data.blocks.length > 0 && (
            <div className="border-line2 border-t px-4 py-3">
              <p className="text-muted mb-2 text-[12px]">
                Blocks are not arithmetic. Any one of these refuses the send whatever the counts
                say.
              </p>
              <ul className="text-text space-y-1.5 text-[12.5px]">
                {quota.data.blocks.map((block, index) => (
                  <li key={index}>
                    <span className="text-muted">{block.source.toLowerCase().replace('_', ' ')}</span>{' '}
                    — {block.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}

      <Panel
        title="Per mailbox"
        description="§21 ceilings and the working window each mailbox runs inside"
      >
        {s.mailboxes.length === 0 ? (
          <Blank
            icon="mailboxes"
            title="No mailboxes are connected."
            body="Mailbox limits are the tightest constraint in most configurations, so this table is where the real ceiling will usually come from."
          />
        ) : (
          <Table>
            <THead>
              <Th>Mailbox</Th>
              <Th width={100} align="right">
                Daily
              </Th>
              <Th width={100} align="right">
                Hourly
              </Th>
              <Th width={120} align="right">
                Gap
              </Th>
              <Th width={130}>Timezone</Th>
              <Th width={150}>Domain ceiling</Th>
            </THead>
            <TBody>
              {s.mailboxes.map((mailbox) => (
                <Tr key={mailbox.id}>
                  <Td mono>{mailbox.email}</Td>
                  <Td align="right" className="tabular-nums">
                    {mailbox.dailyLimit}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {mailbox.hourlyLimit ?? <span className="text-faint">none</span>}
                  </Td>
                  <Td align="right" className="text-muted tabular-nums">
                    {mailbox.minimumGapSec}–{mailbox.maximumGapSec}s
                  </Td>
                  <Td className="text-muted">{mailbox.timezone}</Td>
                  <Td className="text-muted tabular-nums">
                    {mailbox.domain.dailyLimit ?? <span className="text-faint">none</span>}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Panel
        title="Per provider"
        description="§80. The technical ceiling is what the provider accepts; the allowance is what this organization permits itself — never the same number"
      >
        {s.policies.length === 0 ? (
          <Blank
            icon="empty"
            title="No provider policies are recorded."
            body="One is written per provider account, which needs credentials first."
          />
        ) : (
          <Table>
            <THead>
              <Th>Provider</Th>
              <Th width={140}>Account type</Th>
              <Th width={140} align="right">
                Technical
              </Th>
              <Th width={150} align="right">
                Allowed here
              </Th>
              <Th width={140}>Reviewed</Th>
            </THead>
            <TBody>
              {s.policies.map((policy) => (
                <Tr key={policy.id}>
                  <Td>{policy.providerAccount.label}</Td>
                  <Td className="text-muted">
                    {policy.accountType ?? <span className="text-faint">—</span>}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {policy.technicalQuota ?? <span className="text-faint">unknown</span>}
                  </Td>
                  <Td align="right" className="tabular-nums">
                    {policy.organizationQuota ?? <span className="text-faint">unset</span>}
                  </Td>
                  <Td className="text-muted">
                    {policy.policyReviewedAt ? (
                      new Date(policy.policyReviewedAt).toLocaleDateString()
                    ) : (
                      <span className="text-faint">never</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Panel title="Per campaign" description="Campaigns that are ready, running or paused">
        {s.campaigns.length === 0 ? (
          <Blank
            icon="campaigns"
            title="No campaign is ready to run."
            body="A campaign in draft imposes no schedule."
          />
        ) : (
          <Table>
            <THead>
              <Th>Campaign</Th>
              <Th width={130}>State</Th>
              <Th width={110}>Mode</Th>
              <Th width={110} align="right">
                Daily
              </Th>
              <Th width={170}>Starts</Th>
            </THead>
            <TBody>
              {s.campaigns.map((campaign) => (
                <Tr key={campaign.id}>
                  <Td>{campaign.name}</Td>
                  <Td className="text-muted">{campaign.state.toLowerCase()}</Td>
                  <Td className="text-muted">{campaign.dryRun ? 'dry run' : 'live'}</Td>
                  <Td align="right" className="tabular-nums">
                    {campaign.dailyLimit ?? <span className="text-faint">inherited</span>}
                  </Td>
                  <Td className="text-muted">
                    {campaign.scheduledStartAt ? (
                      new Date(campaign.scheduledStartAt).toLocaleString()
                    ) : (
                      <span className="text-faint">not scheduled</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>
    </Screen>
  )
}
